import { useCallback, useEffect, useRef, useState } from "react";
import {
  checkpointFeedbackOpenTime,
  feedbackInvitationEligibility,
  readFeedbackInvitationState,
  recordAutomaticFeedbackInvitationPresented,
  recordAutomaticFeedbackRejection,
  registerFeedbackAppOpen,
  resumeFeedbackOpenTime,
} from "./feedbackInvitationPersistence";

export const FEEDBACK_INVITATION_MIN_MS = 3 * 60 * 1000;
export const FEEDBACK_INVITATION_MAX_MS = 5 * 60 * 1000;
const nextDelay = () => FEEDBACK_INVITATION_MIN_MS + Math.floor(Math.random() * (FEEDBACK_INVITATION_MAX_MS - FEEDBACK_INVITATION_MIN_MS + 1));

export const useActiveFeedbackInvitation = audience => {
  const [showInvitation, setShowInvitation] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [scheduleVersion, setScheduleVersion] = useState(0);
  const remainingMs = useRef(nextDelay());
  const activeSince = useRef(null);
  const activeTimer = useRef(null);
  const openedAudiences = useRef(new Set());

  const pauseActiveTimer = useCallback(() => {
    if (activeTimer.current === null || activeSince.current === null) return;
    remainingMs.current = Math.max(0, remainingMs.current - (Date.now() - activeSince.current));
    clearTimeout(activeTimer.current); activeTimer.current = null; activeSince.current = null;
  }, []);
  const resumeActiveTimer = useCallback(() => {
    if (!audience || activeTimer.current !== null || isEligible || showInvitation || document.visibilityState === "hidden") return;
    const state = readFeedbackInvitationState(audience);
    if (state.presentationCount > 0 || (state.pauseUntil && Date.now() < state.pauseUntil)) return;
    activeSince.current = Date.now();
    activeTimer.current = setTimeout(() => {
      activeTimer.current = null; activeSince.current = null;
      const latest = readFeedbackInvitationState(audience);
      if (!(latest.pauseUntil && Date.now() < latest.pauseUntil)) setIsEligible(true);
    }, remainingMs.current);
  }, [audience, isEligible, showInvitation]);

  useEffect(() => {
    if (!audience) return;
    if (!openedAudiences.current.has(audience)) {
      registerFeedbackAppOpen(audience);
      openedAudiences.current.add(audience);
    }
    remainingMs.current = nextDelay();
    setShowInvitation(false);
    setIsEligible(feedbackInvitationEligibility(audience).eligible);
    const onPageHide = () => checkpointFeedbackOpenTime(audience, Date.now(), true);
    const onPageShow = () => resumeFeedbackOpenTime(audience);
    const checkpointId = setInterval(() => {
      const result = feedbackInvitationEligibility(audience);
      if (result.eligible) setIsEligible(true);
    }, 60 * 1000);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => { clearInterval(checkpointId); window.removeEventListener("pagehide", onPageHide); window.removeEventListener("pageshow", onPageShow); checkpointFeedbackOpenTime(audience, Date.now(), true); };
  }, [audience]);

  useEffect(() => {
    if (!audience) return;
    const state = readFeedbackInvitationState(audience);
    const remainingLong = Math.max(0, state.nextLongIntervalMs - state.accumulatedOpenMs);
    const id = setTimeout(() => {
      const result = feedbackInvitationEligibility(audience);
      if (result.eligible) setIsEligible(true);
    }, remainingLong);
    return () => clearTimeout(id);
  }, [audience, scheduleVersion]);

  useEffect(() => {
    const onVisibility = () => document.visibilityState === "hidden" ? pauseActiveTimer() : resumeActiveTimer();
    onVisibility(); document.addEventListener("visibilitychange", onVisibility);
    return () => { document.removeEventListener("visibilitychange", onVisibility); pauseActiveTimer(); };
  }, [pauseActiveTimer, resumeActiveTimer]);

  const completeSignificantInteraction = useCallback(() => {
    if (!audience || !isEligible || showInvitation) return;
    recordAutomaticFeedbackInvitationPresented(audience);
    setIsEligible(false); setShowInvitation(true); setScheduleVersion(value => value + 1);
  }, [audience, isEligible, showInvitation]);
  const rejectInvitation = useCallback(() => {
    if (audience) recordAutomaticFeedbackRejection(audience);
    setShowInvitation(false);
  }, [audience]);
  const acceptInvitation = useCallback(() => setShowInvitation(false), []);
  const deferInvitation = useCallback(() => setShowInvitation(false), []);

  return { showInvitation, rejectInvitation, acceptInvitation, completeSignificantInteraction, deferInvitation };
};
